/**
 * Headlight Bridge Inspection — the first Headlight micro app.
 *
 * This app has almost no code of its own, and that is the point. It IS the Headlight platform
 * configuration application — same views, same providers, same styling, same boot sequence — with a
 * bridge-shaped surface laid over it by pict-microapp: the bridge dashboard as the landing screen,
 * a five-section menu, and a route allow list that exposes the inspection, bridge, team, document
 * and observation screens and nothing else.
 *
 * The practical consequence: a fix or a feature that lands in the platform app shows up here on the
 * next build, with no porting. Adding a screen to this app is one line in the manifest.
 *
 * Three files make up the whole app:
 *   BridgeInspection-MicroApp-Manifest.js   what the app exposes (routes, record sets, branding)
 *   BridgeInspection-Navigation-Graph.js    the menu
 *   this file                               the wiring, plus bridge-specific login branding
 *
 * The platform app is required by relative path rather than as a package dependency — the same way
 * it reaches into web/lims and web/headlight-form-editor. The build resolves it through the Docker
 * context (see the Dockerfile's ADD list), so browserify can walk its graph.
 */

const libPlatformConfiguration = require('../../headlight-platform-configuration/source/PlatformConfiguration-Application-Web.js');
const libPictMicroApp = require('pict-microapp');

const libBridgeManifest = require('./BridgeInspection-MicroApp-Manifest.js');

/**
 * Bridge branding for the shared Headlight login page.
 *
 * The platform app holds its own login options in a module-level constant and applies them at each
 * of the three places it can show the login screen, so they cannot be overridden by configuration.
 * `_brandLoginPage()` below wraps the login view's own customize method instead, which catches all
 * three call sites at once.
 */
const BRIDGE_LOGIN_PAGE_OPTIONS =
{
	title: 'Bridge Inspection',
	titleStyles: `
		z-index: 1;
		text-shadow:  5px 9px 2px var(--bulma-black);
		word-wrap: break-word;
		overflow-wrap: break-word;
		text-align: center;
		font-size: 4rem !important;
		margin: 0 0 5vh 0;`,
	wallpaperStyles: `
		background-image: url('../../images/headlight-bridge.jpg');
		background-size: cover;
		background-repeat: no-repeat;
		background-color: var(--bulma-grey);
		background-blend-mode: multiply;`,
	closePanelsOnLogout: true
};

/**
 * How pict-microapp binds the manifest to the platform application's own naming.
 *
 * `AttachOn` matters most: the route gate has to be installed after the platform app has created
 * its router provider (during onInitializeAsync) and before that router registers its routes
 * (during the login / data-load cycle). onAfterInitializeAsync sits exactly between the two.
 */
const BRIDGE_COMPOSE_OPTIONS =
{
	RouterProviderHash: 'PictRouter',
	HostRouterProviderHash: 'PlatformConfigurationRouter',
	NavigationProviderHash: 'Pict-Navigation',
	AttachOn: 'onAfterInitializeAsync',
	NavigationGateMethod: '_applyCustomerNavGate',
	HostPermittedMethod: '_navItemPermitted',
	IsPrivilegedMethod: '_isSuperUser'
};

class BridgeInspection extends libPictMicroApp.composeMicroApp(libPlatformConfiguration, libBridgeManifest, BRIDGE_COMPOSE_OPTIONS)
{
	/**
	 * The platform app registers its login view here, so this is the first moment the branding
	 * wrapper can be installed — before any of the three code paths that show the login page run.
	 *
	 * @return {any} the base class result
	 */
	onInitialize()
	{
		const tmpResult = super.onInitialize();
		this._brandLoginPage();
		return tmpResult;
	}

	/**
	 * Wrap the login view's customize method so the platform's options are applied first and the
	 * bridge's branding lands on top, wherever the platform app decides to show the login screen.
	 *
	 * @return {Boolean} whether the wrapper was installed
	 */
	/**
	 * This app's documentation, replacing the platform configuration app's.
	 *
	 * Without this override a bridge inspector opening the Academy lands on "Platform Configuration"
	 * and a table of contents about Master Lists, Pay Items and Customer Provisioning — the host app's
	 * docs, shipped verbatim into this app's serve folder. The content itself lives centrally in
	 * docs/academy/bridge-inspection/ and is copied in at build time (see package.json copyFiles).
	 *
	 * ContentHashPrefix is the load-bearing one: AcademyContent has no application column, so the Hash
	 * namespace is the ONLY thing separating this app's rows from the config app's. Sharing a prefix
	 * would mean each app's doc push silently overwrote the other's.
	 */
	get documentationProfile()
	{
		return (
			{
				DocsBaseURL: 'docs/',
				TopicsURL: 'docs/_topics.json',
				LandingArticle: 'overview.md',
				ContentHashPrefix: 'Doc-BridgeApp-',
				Title: 'Bridge Academy',
				Subtitle: 'Guides for inspecting, scheduling and reporting on bridges.',
			});
	}

	_brandLoginPage()
	{
		const tmpLoginView = this.pict.views.HeadlightLogin;
		if (!tmpLoginView || (typeof tmpLoginView.customizeLoginPage !== 'function') || tmpLoginView._bridgeBranded)
		{
			return false;
		}
		const fPlatformCustomize = tmpLoginView.customizeLoginPage.bind(tmpLoginView);
		tmpLoginView.customizeLoginPage = (pOptions) => fPlatformCustomize(Object.assign({}, pOptions, BRIDGE_LOGIN_PAGE_OPTIONS));
		tmpLoginView._bridgeBranded = true;
		return true;
	}
}

module.exports = BridgeInspection;

// The platform app's configuration is reused wholesale — its 164 form manifests, its ~60 record-set
// configurations, its field decoration and its PSRS wiring are exactly what the bridge screens run
// on. Only the product identity changes, so the server knows which app is asking.
module.exports.default_configuration = Object.assign({}, libPlatformConfiguration.default_configuration,
	{
		Name: 'Bridge Inspection',
		Hash: 'BridgeInspection'
	});

module.exports.default_configuration.pict_configuration = Object.assign({},
	libPlatformConfiguration.default_configuration.pict_configuration,
	{
		Product: 'BridgeInspection'
	});
